import { AxiosError } from "axios";
import { apiClient } from "@/lib/api-client";
import { parseContentDispositionFilename } from "../lib/parse-content-disposition";
import { extractExportErrorMessage } from "../lib/extract-export-error-message";
import type { ApiResponse } from "@/types/api";
import type {
  ApplyOverrideRequest,
  ExportManifestDto,
  ExportType,
  ForcePlaceRequest,
  ForcePlaceResult,
  ReassignOption,
  Run,
  RunSummary,
} from "../types";

/**
 * POSTs to `/api/schedules/{scheduleId}/runs` to create a new scheduler run.
 * The backend orchestrates section resolution → greedy assignment → Stage 3
 * validation in a single transaction and returns the full {@link Run}
 * (including the assignments collection) on success.
 *
 * Failure modes worth distinguishing at the call site (see {@link useCreateRun}):
 *   - **400** with `errorCode = "run.preflight_failed"` — Stage 1/2 errors
 *     are still outstanding. Surfaced to the user with a specific toast.
 *   - **401 / 403** — auth / role checks. Falls through to the generic axios
 *     interceptors.
 *   - **404** — cross-schedule lookup mismatch (unlikely from a happy-path UI).
 */
export async function createRun(
  scheduleId: string,
  timeLimitSeconds?: number,
): Promise<Run> {
  // Body carries the optional per-run solver time-limit override; omitting it
  // (or sending null) keeps the configured default (~10 min). A positive value
  // lets an on-demand run search longer — e.g. 36000 for a 10-hour overnight run.
  const body =
    timeLimitSeconds && timeLimitSeconds > 0
      ? { timeLimitSeconds }
      : { timeLimitSeconds: null };
  const response = await apiClient.post<ApiResponse<Run>>(
    `/schedules/${scheduleId}/runs`,
    body,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to create run");
}

/**
 * GETs the run list for a schedule. The backend orders by `StartedAt DESC`
 * (latest first) — consumers can rely on `data[0]` being the most recent run.
 */
export async function listRuns(scheduleId: string): Promise<RunSummary[]> {
  const response = await apiClient.get<ApiResponse<RunSummary[]>>(
    `/schedules/${scheduleId}/runs`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load runs");
}

/**
 * GETs a single run with its full assignments collection.
 */
export async function getRun(scheduleId: string, runId: string): Promise<Run> {
  const response = await apiClient.get<ApiResponse<Run>>(
    `/schedules/${scheduleId}/runs/${runId}`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load run");
}

/**
 * GETs reassignment candidate options for a (student, course) pair on a given
 * run. Backend computes fit status server-side; the client just renders.
 */
export async function getReassignOptions(
  scheduleId: string,
  runId: string,
  studentIdentifier: string,
  courseId: string,
): Promise<ReassignOption[]> {
  const params = new URLSearchParams({
    studentIdentifier,
    courseId,
  });
  const response = await apiClient.get<ApiResponse<ReassignOption[]>>(
    `/schedules/${scheduleId}/runs/${runId}/reassign-options?${params.toString()}`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load reassign options");
}

/**
 * POSTs an override Assignment for the given run. Backend rejects with a
 * 400 + errorCode if the run is not latest or not Succeeded; with a 404 if
 * the target section can't be resolved.
 */
/**
 * GETs the Phase 6 export manifest for a run — a single round-trip read that
 * tells the export modal which cards to render and what the run's display
 * number is. Same `[Authorize]` base auth as the other run reads (Viewer can
 * read; only the download endpoints in Slice 5 require OrgAdmin+).
 */
export async function getExportManifest(
  scheduleId: string,
  runId: string,
): Promise<ExportManifestDto> {
  const response = await apiClient.get<ApiResponse<ExportManifestDto>>(
    `/schedules/${scheduleId}/runs/${runId}/export/manifest`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load export manifest");
}

export async function applyOverride(
  scheduleId: string,
  runId: string,
  request: ApplyOverrideRequest,
): Promise<Run> {
  const response = await apiClient.post<ApiResponse<Run>>(
    `/schedules/${scheduleId}/runs/${runId}/assignments`,
    request,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to apply override");
}

/**
 * POSTs the "accept small overlaps" force-place action. With `dryRun: true` the
 * backend returns the plan (who would be placed / skipped) without writing;
 * `dryRun: false` commits the placements. A stale/superseded run yields a 400
 * with `errorCode = "override.not_latest_run"` (surfaced at the call site).
 */
export async function forcePlaceOverlaps(
  scheduleId: string,
  runId: string,
  request: ForcePlaceRequest,
): Promise<ForcePlaceResult> {
  const response = await apiClient.post<ApiResponse<ForcePlaceResult>>(
    `/schedules/${scheduleId}/runs/${runId}/force-place-overlaps`,
    request,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to force-place students");
}

/**
 * Per-export-type metadata: backend path segment, the Accept header to send,
 * and the fallback filename to use if the server's `Content-Disposition` is
 * missing (defense-in-depth — the controllers always emit one, but a future
 * proxy might strip it).
 */
export const EXPORT_ENDPOINTS = {
  overall: {
    path: "overall.ics",
    accept: "text/calendar",
    fallbackFilename: "schedule.ics",
  },
  // Optional split delivery: the same calendar as a zip of ~1 MB
  // overall-part-NN.ics files, for when a single .ics is too large for Outlook.
  "overall-split": {
    path: "overall-split.zip",
    accept: "application/zip",
    fallbackFilename: "schedule-overall-split.zip",
  },
  "per-teacher": {
    path: "per-teacher.zip",
    accept: "application/zip",
    fallbackFilename: "per-teacher.zip",
  },
  "per-student": {
    path: "per-student.zip",
    accept: "application/zip",
    fallbackFilename: "per-student.zip",
  },
  "per-room": {
    path: "per-room.zip",
    accept: "application/zip",
    fallbackFilename: "per-room.zip",
  },
} as const satisfies Record<
  ExportType,
  { path: string; accept: string; fallbackFilename: string }
>;

export interface ExportDownloadResult {
  blob: Blob;
  filename: string;
}

/**
 * Fetches one export bundle as a Blob, returning the parsed filename from
 * `Content-Disposition` alongside (with a fallback to a type-specific default
 * when the header is missing or malformed).
 *
 * Routes through the shared `apiClient` so the Firebase ID token is attached
 * by the request interceptor — same auth surface as every other call in this
 * file. `responseType: 'blob'` tells axios to skip JSON parsing on the success
 * path, but the response interceptor still surfaces 401s and the like.
 *
 * Errors are normalized to plain `Error` instances with user-facing messages
 * pulled from the `ApiResponse` JSON body (or a friendly 403 string). The
 * caller — `useDownloadExport` — sets these on per-card error state.
 */
export async function downloadExportBundle(
  scheduleId: string,
  runId: string,
  type: ExportType,
  organizer?: string,
): Promise<ExportDownloadResult> {
  const endpoint = EXPORT_ENDPOINTS[type];
  // The organizer override only applies to the overall (master BSC calendar)
  // exports — that's the file Abby imports and uses "Send Updates" from. The
  // per-teacher/-student/-room bundles ignore it.
  const trimmedOrganizer = organizer?.trim();
  const supportsOrganizer = type === "overall" || type === "overall-split";
  const params =
    supportsOrganizer && trimmedOrganizer
      ? { organizer: trimmedOrganizer }
      : undefined;
  try {
    const response = await apiClient.get<Blob>(
      `/schedules/${scheduleId}/runs/${runId}/export/${endpoint.path}`,
      {
        responseType: "blob",
        headers: { Accept: endpoint.accept },
        params,
      },
    );
    const disposition = response.headers["content-disposition"];
    const dispositionHeader =
      typeof disposition === "string" ? disposition : null;
    const filename =
      parseContentDispositionFilename(dispositionHeader) ??
      endpoint.fallbackFilename;
    return { blob: response.data, filename };
  } catch (err) {
    if (err instanceof AxiosError) {
      const status = err.response?.status ?? 0;
      // Error responses come back as Blob too (we asked for `responseType: blob`).
      // Read the text and try to JSON-parse so the user-facing message from the
      // backend's `ApiResponse<object>` shape surfaces correctly.
      const data = err.response?.data;
      let parsed: unknown = data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          parsed = text.length > 0 ? JSON.parse(text) : null;
        } catch {
          parsed = null;
        }
      }
      throw new Error(extractExportErrorMessage(parsed, status));
    }
    throw err;
  }
}
